import { createQueryClient } from '@Core/clients/queryClient';
import { isInsideEditorPreviewFrame } from '@Core/lib/pageEditorPreview';
import { matchRoutePath } from '@Core/utils/routeParams';
import { router, usePage } from '@inertiajs/react';
import { PageHead } from '@Pages/components/PageHead';
import { useEmbedHeightReporter } from '@Pages/hooks/useEmbedHeightReporter';
import { usePrintPage } from '@Pages/hooks/usePrintPage';
import { PageScrollContainer } from '@Pages/layouts/PageScrollContainer';
import { useEditorPreviewBridge } from '@Pages/pageEditor/preview/useEditorPreviewBridge';
import {
  BreadcrumbLocationContext,
  rememberBreadcrumbLocation
} from '@Pages/pageRuntime/breadcrumbLocations';
import type { PageSdkRenderSurface } from '@Pages/pageRuntime/compiler/reportPageSdkError';
import { preloadAiAgentButton } from '@Pages/pageRuntime/components/registry';
import { registerIconNodes } from '@Pages/pageRuntime/components/ui/dynamicIconNodes';
import { preloadDynamicIcons } from '@Pages/pageRuntime/components/ui/preloadDynamicIcons';
import { layoutSourcePath, pageSourcePath } from '@Pages/pageRuntime/designMode/sourceMetadata';
import { subscribeToQueryParamChanges } from '@Pages/pageRuntime/hooks/useQueryParams';
import { PageRenderer } from '@Pages/pageRuntime/PageRenderer';
import { PageRuntimeProvider } from '@Pages/pageRuntime/PageRuntimeContext';
import { usePageRuntime } from '@Pages/pageRuntime/pageRuntimeContextValue';
import { createPageSdkRequestTracker } from '@Pages/pageRuntime/sdk/requestActivity';
import type {
  PageResourceData,
  SdkAuthConfig,
  SdkNavigationMenuCatalog,
  SdkIdentity,
  SdkWorkspaceField,
  SdkWorkspaceRecord
} from '@Pages/pageRuntime/sdk/types';
import { QueryClientProvider, useIsFetching } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { GlobalValidationErrorModal } from '@/components/GlobalValidationErrorModal';
import { HttpRequestErrorModal } from '@/components/HttpRequestErrorModal';
import { ImpersonationTopBanner } from '@/components/ImpersonationTopBanner';
import { useLogRocketIdentify } from '@/hooks/useLogRocketIdentify';
import { useSentryIdentify } from '@/hooks/useSentryIdentify';
import { FlashMessages } from '@/layouts/FlashMessages';
import type { PageProps } from '@/types';
import { PageRenderProfile } from '@/types/enums';

type ShowPagePageProps = Diagonal.Pages.PageProps.ShowPagePageProps;

type Props = PageProps<
  Omit<
    ShowPagePageProps,
    | 'resources'
    | 'sdkWorkspace'
    | 'sdkWorkspaceFields'
    | 'sdkNavigation'
    | 'sdkIdentity'
    | 'sdkAuth'
    | 'sharedComponents'
    | 'workflowRouteSettings'
    | 'pageRouteSettings'
  > & {
    resources: Record<string, PageResourceData>;
    sdkWorkspace: SdkWorkspaceRecord;
    sdkWorkspaceFields: SdkWorkspaceField[];
    sdkNavigation: SdkNavigationMenuCatalog;
    sdkIdentity: SdkIdentity;
    sdkAuth: SdkAuthConfig;
    sharedComponents: Array<{
      id?: string;
      name: string;
      code: string;
    }>;
    workflowRouteSettings: Record<string, Diagonal.Workflows.Data.WorkflowRouteSettingsData> | null;
    pageRouteSettings: Record<string, Diagonal.Pages.Data.PageRouteSettingsData> | null;
  }
>;

const queryClient = createQueryClient();

let aiPageShellModule: Promise<typeof import('@Pages/pageRuntime/components/AiPageShell')> | null =
  null;
const preloadAiPageShell = () =>
  (aiPageShellModule ??= import('@Pages/pageRuntime/components/AiPageShell'));
const AiPageShell = lazy(async () => ({
  default: (await preloadAiPageShell()).AiPageShell
}));

function usesAiAgentButton(sources: Array<string | null | undefined>): boolean {
  return sources.some((source) => source && /\bAiAgentButton\b/.test(source));
}

/**
 * The route param values this document is actually rendering, or null when its
 * URL is not the page's authored route — the `/preview/pages/{page}` URL the
 * editor first frames, or anywhere a redirect landed. Only a concrete location
 * may be adopted by the editor, so a draft-preview URL never overwrites the
 * author's own preview inputs.
 */
function routedLocation(
  url: string,
  pageId: string,
  pageRouteSettings: Props['pageRouteSettings']
): Record<string, string> | null {
  const route = pageRouteSettings?.[pageId]?.route;

  if (!route) {
    return null;
  }

  return matchRoutePath(route, url.split('?')[0]);
}

/**
 * Whether the server rendered this document as a draft preview — the editor's
 * iframe and the shareable token preview session. Those are the documents whose
 * shell emits an empty `<style id="workspace-preview-stylesheet">` instead of a
 * workspace stylesheet link, so the preview worker owns their Tailwind CSS as
 * well as their transform.
 *
 * Narrower than the `isPreviewSurface` this file also computes, which is derived
 * from the render surface and is true for a published page framed by the
 * editor. The worker transforms that page too — the editor can hand it unsaved
 * source — but it carries the stylesheet its own render resolved.
 */
function isServerDraftPreview(
  isAuthoringPreview: boolean | undefined,
  isDraftPreview: boolean | undefined
): boolean {
  return isAuthoringPreview === true || isDraftPreview === true;
}

function resolveRenderSurface(
  isAuthoringPreview: boolean | undefined,
  isDraftPreview: boolean | undefined
): PageSdkRenderSurface {
  if (isAuthoringPreview) {
    return 'editor-preview';
  }

  if (isDraftPreview === true || isInsideEditorPreviewFrame()) {
    return 'draft-preview';
  }

  return 'published';
}

/**
 * A page file's draft runs the loader of the workspace modules it imports
 * before its own code, because the editor edits the code without the imports.
 */
function withPrelude(prelude: string | null | undefined, code: string): string {
  return prelude ? `${prelude}\n${code}` : code;
}

/**
 * Stores the page's URL when the visitor opens the page, when page code writes query params, and
 * when the visitor leaves the page, so a breadcrumb back to the page returns to the filters and
 * query params last in use. A browser Back or Forward changes the location before any event, so
 * the query param write is the only point that sees the leaving page's last URL.
 */
function useBreadcrumbLocationRecording(workspaceId: string | null): void {
  const { url } = usePage();

  useEffect(() => {
    if (workspaceId === null) {
      return;
    }

    const remember = () => rememberBreadcrumbLocation(workspaceId);
    remember();

    const removeBeforeListener = router.on('before', remember);
    const removeQueryParamListener = subscribeToQueryParamChanges(remember);
    window.addEventListener('pagehide', remember);

    return () => {
      removeBeforeListener();
      removeQueryParamListener();
      window.removeEventListener('pagehide', remember);
    };
  }, [workspaceId, url]);
}

function ShowPage({
  isAuthoringPreview,
  isDraftPreview,
  pageCode,
  pageModules,
  pagePrelude,
  pageId,
  renderProfile,
  pageData,
  files,
  fileHosts,
  icons,
  resources,
  resourceIdsBySlug,
  sdkWorkspace,
  sdkWorkspaceFields,
  sdkNavigation,
  sdkIdentity,
  sdkAuth,
  sharedComponents,
  workflowRouteSettings,
  pageRouteSettings,
  identity: authenticatedIdentity
}: Props) {
  registerIconNodes(icons);
  const contentRef = useRef<HTMLDivElement>(null);
  const renderSurface = resolveRenderSurface(isAuthoringPreview, isDraftPreview);
  const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
  const [readyPageId, setReadyPageId] = useState<string | null>(null);
  // The layout builds one SDK for the layout and page renderers, so imperative
  // requests are counted by the shared runtime's tracker. The layout remounts
  // that runtime per page, which resets the tracker with the page. The
  // standalone tracker only serves a page mounted without its layout.
  const runtime = usePageRuntime();
  const [standaloneRequestTracker] = useState(createPageSdkRequestTracker);
  const sdkRequestTracker = runtime?.requestTracker ?? standaloneRequestTracker;
  // The first commit reports the page as rendered while SDK data is still loading,
  // so a Document that lists records would print its loading state. Hold the print
  // until both React Query hooks and imperative SDK requests have settled. The query
  // client is explicit because tests mount ShowPage without its layout.
  const pendingQueries = useIsFetching(undefined, queryClient);
  const pendingSdkRequests = useSyncExternalStore(
    sdkRequestTracker.subscribe,
    sdkRequestTracker.getSnapshot,
    sdkRequestTracker.getSnapshot
  );
  usePrintPage(
    pageId,
    renderProfile === PageRenderProfile.Print &&
      renderSurface === 'published' &&
      !isEmbedded &&
      readyPageId === pageId &&
      pendingQueries === 0 &&
      pendingSdkRequests === 0
  );
  // Read from Inertia's url rather than `window.location`, so an in-preview
  // navigation re-derives the location: `window.location` is not a reactive
  // input and a render-time read of it would go stale after the first page.
  const { url } = usePage();
  useEmbedHeightReporter(pageId, contentRef, {
    isEnabled: renderSurface === 'published'
  });
  const editorPreview = useEditorPreviewBridge(
    pageId,
    authenticatedIdentity
      ? {
          id: authenticatedIdentity.id,
          resourceId: authenticatedIdentity.resource_id,
          displayName: authenticatedIdentity.$display_name
        }
      : null,
    routedLocation(url, pageId, pageRouteSettings)
  );
  const handlePageRendered = () => {
    setReadyPageId(pageId);
  };
  const isEditorPreview = renderSurface === 'editor-preview';
  const authoringSourcePath = isEditorPreview ? pageSourcePath(pageId) : undefined;

  return (
    <div ref={contentRef} className="w-full max-w-full">
      <PageRenderer
        code={withPrelude(pagePrelude, editorPreview.code ?? pageCode)}
        module={
          pageModules && editorPreview.code === null
            ? { specifier: pageModules.page, features: pageModules.features }
            : null
        }
        data={pageData}
        files={files}
        fileHosts={fileHosts}
        eagerImages={renderProfile === PageRenderProfile.Print}
        runtimeKey={pageId}
        resources={resources}
        resourceIdsBySlug={resourceIdsBySlug}
        workspace={sdkWorkspace}
        workspaceFields={sdkWorkspaceFields}
        navigation={sdkNavigation}
        identity={sdkIdentity}
        auth={sdkAuth}
        sharedComponents={sharedComponents}
        workflowRouteSettings={workflowRouteSettings}
        pageRouteSettings={pageRouteSettings}
        authoringSourcePath={authoringSourcePath}
        sourcePath={pageSourcePath(pageId)}
        renderSurface={renderSurface}
        compilePreviewStylesheet={isServerDraftPreview(isAuthoringPreview, isDraftPreview)}
        onRendered={handlePageRendered}
        requestTracker={sdkRequestTracker}
        designMode={editorPreview.designMode}
      />
    </div>
  );
}

type PageLayoutProps = Props & {
  children: React.ReactNode;
};

function PageLayout({
  children,
  isAuthoringPreview,
  isDraftPreview,
  pageCode,
  pageId,
  renderProfile,
  workspace,
  meta,
  pageTitle,
  pageDescription,
  pageImage,
  pageCanonicalUrl,
  pageData,
  files,
  fileHosts,
  icons,
  preloadIconMap,
  resources,
  resourceIdsBySlug,
  sdkWorkspace,
  sdkWorkspaceFields,
  sdkNavigation,
  sdkIdentity,
  sdkAuth,
  sharedComponents,
  referencedSharedComponentNames,
  layoutComponent,
  pageModules,
  workflowRouteSettings,
  pageRouteSettings
}: PageLayoutProps) {
  // The server sends the shapes of the icons this render names. They must be
  // registered before a descendant renders so those icons are in the first
  // commit; the layout renders first, and a page mounted without it registers
  // its own.
  registerIconNodes(icons);

  if (preloadIconMap) {
    preloadDynamicIcons();
  }

  const title = pageTitle || meta?.title;
  const description = pageDescription || '';
  const image = pageImage || '';
  const canonicalUrl = pageCanonicalUrl;
  const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
  const renderSurface = resolveRenderSurface(isAuthoringPreview, isDraftPreview);
  const isPrintProfile = renderProfile === PageRenderProfile.Print;
  const isPreviewSurface = renderSurface !== 'published';
  const isEditorPreview = renderSurface === 'editor-preview';
  const isServerDraft = isServerDraftPreview(isAuthoringPreview, isDraftPreview);
  useLogRocketIdentify(!isPrintProfile);
  useSentryIdentify(!isPrintProfile);
  // The editor preview frame shares session storage with the editor tab, so a previewed page
  // must not store locations for that tab.
  const breadcrumbWorkspaceId =
    sdkWorkspace?.id && !isPrintProfile && !isEditorPreview && !isInsideEditorPreviewFrame()
      ? sdkWorkspace.id
      : null;
  useBreadcrumbLocationRecording(breadcrumbWorkspaceId);
  // A built page renders its layout module; a page from source compiles the layout code.
  const layoutSource = pageModules?.layout
    ? {
        code: null,
        module: { specifier: pageModules.layout, features: pageModules.features },
        authoringSourcePath: undefined,
        sourcePath: pageModules.layout
      }
    : layoutComponent?.code
      ? {
          code: layoutComponent.code,
          module: null,
          authoringSourcePath: isEditorPreview ? layoutSourcePath(layoutComponent.id) : undefined,
          sourcePath: layoutSourcePath(layoutComponent.id)
        }
      : null;
  const pageContent = (
    <div
      className="isolate"
      data-page-embed-content={isEmbedded && !isPreviewSurface ? '' : undefined}
    >
      <PageScrollContainer isDraftPreview={isPreviewSurface} renderProfile={renderProfile}>
        {layoutSource ? (
          <PageRenderer
            code={layoutSource.code}
            module={layoutSource.module}
            data={pageData}
            files={files}
            runtimeKey={pageId}
            resources={resources}
            resourceIdsBySlug={resourceIdsBySlug}
            workspace={sdkWorkspace}
            workspaceFields={sdkWorkspaceFields}
            navigation={sdkNavigation}
            identity={sdkIdentity}
            auth={sdkAuth}
            sharedComponents={sharedComponents}
            workflowRouteSettings={workflowRouteSettings}
            pageRouteSettings={pageRouteSettings}
            sourceKind="layout"
            authoringSourcePath={layoutSource.authoringSourcePath}
            sourcePath={layoutSource.sourcePath}
            renderSurface={renderSurface}
            compilePreviewStylesheet={isServerDraft}
          >
            {children}
          </PageRenderer>
        ) : (
          children
        )}
      </PageScrollContainer>
    </div>
  );
  const rootSources = [pageCode, layoutComponent?.code];
  const referencedSharedComponentNameSet = new Set(referencedSharedComponentNames);
  const aiAgentSources = [...rootSources];

  for (const component of sharedComponents ?? []) {
    if (
      referencedSharedComponentNames == null ||
      referencedSharedComponentNameSet.has(component.name)
    ) {
      aiAgentSources.push(component.code);
    }
  }

  // A page built as modules sends no source text, so its build reports the button.
  const hasAiAgentButton =
    !isPrintProfile &&
    (pageModules
      ? pageModules.features.includes('ai_agent_button')
      : usesAiAgentButton(aiAgentSources));

  if (hasAiAgentButton) {
    void preloadAiPageShell();
    void preloadAiAgentButton();
  }

  return (
    <QueryClientProvider client={queryClient} key={pageId}>
      <PageHead
        title={title}
        description={description}
        image={image}
        canonicalUrl={canonicalUrl}
        workspaceName={workspace?.name}
      />
      {!isPrintProfile ? (
        <>
          <FlashMessages />
          <HttpRequestErrorModal />
          <GlobalValidationErrorModal />
          {!isEmbedded ? <ImpersonationTopBanner /> : null}
        </>
      ) : null}
      <PageRuntimeProvider
        rootSources={rootSources}
        data={pageData}
        files={files}
        fileHosts={fileHosts}
        eagerImages={renderProfile === PageRenderProfile.Print}
        runtimeKey={pageId}
        resources={resources}
        resourceIdsBySlug={resourceIdsBySlug}
        authoringSourcePath={isEditorPreview ? pageSourcePath(pageId) : undefined}
        workspace={sdkWorkspace}
        workspaceFields={sdkWorkspaceFields}
        navigation={sdkNavigation}
        identity={sdkIdentity}
        auth={sdkAuth}
        sharedComponents={sharedComponents}
        workflowRouteSettings={workflowRouteSettings}
        pageRouteSettings={pageRouteSettings}
        transformMode={isPreviewSurface ? 'worker' : 'main-thread'}
        compilePreviewStylesheet={isServerDraft}
      >
        <BreadcrumbLocationContext.Provider value={breadcrumbWorkspaceId}>
          {hasAiAgentButton ? (
            <Suspense fallback={null}>
              <AiPageShell>{pageContent}</AiPageShell>
            </Suspense>
          ) : (
            pageContent
          )}
        </BreadcrumbLocationContext.Provider>
      </PageRuntimeProvider>
    </QueryClientProvider>
  );
}

ShowPage.layout = PageLayout;

export default ShowPage;
